/**
 * Quote Data Storage Helper
 *
 * Uses a hybrid approach: cookie as a lightweight pointer (for expiry + backend compat),
 * localStorage for the actual payload (no 4KB size limit).
 *
 * - Write: stores data in localStorage, sets a "1" cookie as a presence flag
 * - Read: checks cookie exists → if yes, reads from localStorage. If no, clears localStorage.
 * - The cookie retains the same name ("quoteModule.quote_data") so backend SessionReset
 *   can still delete it by name without changes.
 */

const STORAGE_KEY = "quoteModule.quote_data";
const STATES_STORAGE_KEY = "quoteModule.quote_data_states";

/**
 * Save quote data to localStorage and set a flag cookie for expiry.
 * @param {Object} $cookies - vue-cookies instance
 * @param {String} cookieName - cookie key name
 * @param {Object} data - the quote_data object to persist
 * @param {Number} expires - expiry in seconds
 * @param {String} [path="/"] - cookie path
 */
export function saveQuoteData($cookies, cookieName, data, expires, path = "/") {
  try {
    const key = cookieName || STORAGE_KEY;
    localStorage.setItem(key, JSON.stringify(data));
    // Set a lightweight flag cookie for expiry control
    $cookies.set(key, "1", expires, path);
  } catch (e) {
    // localStorage full or disabled — fall back to cookie-only (may truncate)
    const key = cookieName || STORAGE_KEY;
    $cookies.set(key, JSON.stringify(data), expires, path);
  }
}

/**
 * Read quote data — checks cookie presence, then reads from localStorage.
 * If cookie is expired/missing, clears localStorage and returns null.
 * @param {Object} $cookies - vue-cookies instance
 * @param {String} cookieName - cookie key name
 * @returns {Object|null} parsed quote data or null
 */
export function loadQuoteData($cookies, cookieName) {
  const key = cookieName || STORAGE_KEY;
  const cookieVal = $cookies.get(key);

  if (!cookieVal) {
    // Cookie expired or cleared — nuke localStorage too
    localStorage.removeItem(key);
    return null;
  }

  // If cookie value is "1" (flag), read from localStorage
  if (cookieVal === "1" || cookieVal === 1) {
    try {
      const stored = localStorage.getItem(key);
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      return null;
    }
  }

  // Backward compat: cookie still holds the full JSON (pre-migration)
  // This handles the transition period where old cookies exist
  if (typeof cookieVal === "object") {
    return cookieVal;
  }

  // String cookie value — try to parse as JSON (legacy format from vue-cookies)
  try {
    return typeof cookieVal === "string" ? JSON.parse(cookieVal) : cookieVal;
  } catch (e) {
    return cookieVal;
  }
}

/**
 * Remove quote data from both localStorage and cookie.
 * @param {Object} $cookies - vue-cookies instance
 * @param {String} cookieName - cookie key name
 */
export function clearQuoteData($cookies, cookieName) {
  const key = cookieName || STORAGE_KEY;
  localStorage.removeItem(key);
  $cookies.remove(key);
}

export { STORAGE_KEY, STATES_STORAGE_KEY };
